import {
    Box,
    Heading,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { PayoutRecipient } from '@common/enums';
import { getActivityNameFromPayoutType } from '@common/utils/getActivityNameFromPayoutType';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { getPayoutRecipientName } from '@frontend/components/pages/processes/adminProcessFinancials/utils/formatPayoutRecipientName';
import { getSelectedLanguage } from '@frontend/translate';
import { currencyFormatter } from '@frontend/utils';
import type { PayoutRecord } from '../adminProcessDetailsTypes';

type PayoutRecordsProps = {
    label: string;
    payoutRecords?: PayoutRecord[];
    showRunNumber: boolean;
};

export function PayoutRecords({ label, payoutRecords, showRunNumber }: PayoutRecordsProps): JSX.Element | null {
    if (!payoutRecords || payoutRecords.length === 0) {
        return null;
    }

    const translate = getSelectedLanguage();

    return (
        <Box marginBottom={3}>
            <Heading
                as="h3"
                fontWeight="bold"
                marginBottom={1}
                size="small"
            >
                {label}
            </Heading>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translate.common.activity}</TableHeader>
                            <TableHeader>{translate.organization.relationNumber}</TableHeader>
                            <TableHeader>{translate.employment.employment}</TableHeader>
                            <TableHeader>{translate.finance.institution}</TableHeader>
                            <TableHeader>{translate.processDetails.performed}</TableHeader>
                            {showRunNumber && <TableHeader>{translate.processDetails.export}</TableHeader>}
                            <TableHeader>{translate.finance.amount}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payoutRecords.map((payoutRecord) => {
                            const payoutRecipientName = getPayoutRecipientName(payoutRecord);

                            return (
                                <TableRow key={payoutRecord.id}>
                                    <TableCell>{getActivityNameFromPayoutType(payoutRecord.payoutType)}</TableCell>
                                    <TableCell>{payoutRecord.relationNumber}</TableCell>
                                    <TableCell>
                                        {payoutRecord.entityToPay === PayoutRecipient.practitioner
                                            ? payoutRecipientName
                                            : ''}
                                    </TableCell>
                                    <TableCell>
                                        {[PayoutRecipient.organization, PayoutRecipient.group].includes(
                                            payoutRecord.entityToPay
                                        )
                                            ? payoutRecipientName
                                            : ''}
                                    </TableCell>
                                    <TableCell>
                                        <NullableFormattedDate value={payoutRecord.date} />
                                    </TableCell>
                                    {showRunNumber && (
                                        <TableCell>{payoutRecord.runNumber + ': ' + payoutRecord.runDate}</TableCell>
                                    )}
                                    <TableCell>{currencyFormatter((payoutRecord.amount || 0) / 100)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
